"use client"

import * as React from "react"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { Eye, EyeOff, Copy, Check, Shuffle } from "lucide-react"

// ── Types ──
type StrengthLevel = 0 | 1 | 2 | 3 | 4;

interface StrengthInfo {
  label: string;
  color: string;
  textColor: string;
}

export interface PasswordInputProps extends React.ComponentProps<typeof Input> {
  showStrengthMeter?: boolean;
  showRequirements?: boolean; // ✅ NEW PROP
  showCopyBtn?: boolean;
  showGenerateBtn?: boolean;
}

// ── Helpers ──
function getPasswordStrength(password: string): StrengthLevel {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return Math.min(score, 4) as StrengthLevel;
}

function generateSecurePassword(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
  let pass = "";
  pass += "A"; pass += "a"; pass += "1"; pass += "!";
  for (let i = pass.length; i < 16; i++) {
    pass += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return pass.split('').sort(() => 0.5 - Math.random()).join('');
}

const strengthConfig: Record<StrengthLevel, StrengthInfo> = {
  0: { label: "", color: "bg-transparent", textColor: "text-transparent" },
  1: { label: "Weak", color: "bg-danger", textColor: "text-danger" },
  2: { label: "Fair", color: "bg-warning", textColor: "text-warning" },
  3: { label: "Good", color: "bg-good", textColor: "text-good" },
  4: { label: "Strong", color: "bg-success", textColor: "text-success" },
};

// const strengthConfig: Record<StrengthLevel, StrengthInfo> = {
//   0: { label: "", color: "bg-transparent", textColor: "text-transparent" },
//   1: { label: "Weak", color: "bg-danger", textColor: "text-danger" },
//   2: { label: "Fair", color: "bg-warning", textColor: "text-warning" },
//   3: { label: "Good", color: "bg-info", textColor: "text-info" },
//   4: { label: "Strong", color: "bg-success", textColor: "text-success" },
// };

// ── Component ──
const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, showStrengthMeter = false, showRequirements = false, showCopyBtn = false, showGenerateBtn = false, onChange, value, ...props }, ref) => {
    const [show, setShow] = React.useState(false);
    const [copied, setCopied] = React.useState(false);

    const handleGenerate = () => {
      const newPassword = generateSecurePassword();
      const fakeEvent = {
        target: { value: newPassword },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange?.(fakeEvent);
    };

    const handleCopy = async () => {
      const currentPassword = typeof value === "string" ? value : "";
      if (!currentPassword) return;
      try {
        await navigator.clipboard.writeText(currentPassword);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy password: ", err);
      }
    };

    const visibleButtonsCount = [showGenerateBtn, showCopyBtn, true].filter(Boolean).length;
    const dynamicPadding = visibleButtonsCount * 40;

    const currentValue = typeof value === "string" ? value : "";
    const strength = getPasswordStrength(currentValue);
    const config = strengthConfig[strength];

    // ✅ NEW: Requirements Checklist Logic
    const requirements = [
      { label: "At least 8 characters", met: currentValue.length >= 8 },
      { label: "One uppercase letter", met: /[A-Z]/.test(currentValue) },
      { label: "One lowercase letter", met: /[a-z]/.test(currentValue) },
      { label: "One number", met: /[0-9]/.test(currentValue) },
      { label: "One special character (!@#$...)", met: /[^A-Za-z0-9]/.test(currentValue) },
    ];

    return (
      <div className="w-full space-y-1.5">
        <div className="relative">
          <Input
            type={show ? "text" : "password"}
            className={cn("transition-all", className)}
            style={{ paddingRight: `${dynamicPadding}px` }}
            value={value}
            onChange={onChange}
            {...props}
            ref={ref}
          />
          
          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
            {showGenerateBtn && (
              <button type="button" onClick={handleGenerate} className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all" title="Generate secure password" tabIndex={-1}>
                <Shuffle className="w-4 h-4" />
              </button>
            )}

            {showCopyBtn && (
              <button type="button" onClick={handleCopy} className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all" title="Copy to clipboard" tabIndex={-1}>
                <div className="relative h-4 w-4">
                  <Check className={cn("w-4 h-4 absolute inset-0 text-success transition-all duration-200", copied ? "opacity-100 scale-100" : "opacity-0 scale-50")} />
                  <Copy className={cn("w-4 h-4 absolute inset-0 transition-all duration-200", copied ? "opacity-0 scale-50" : "opacity-100 scale-100")} />
                </div>
              </button>
            )}

            <button type="button" onClick={() => setShow(!show)} className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all" aria-label={show ? "Hide password" : "Show password"} tabIndex={-1}>
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Strength Meter */}
        {showStrengthMeter && currentValue.length > 0 && (
          <div className="flex items-center gap-2 pt-1">
            <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
              <div className={cn("h-full rounded-full transition-all duration-500 ease-out", config.color)} style={{ width: `${(strength / 4) * 100}%` }} />
            </div>
            <span className={cn("text-sm font-semibold transition-colors duration-300", config.textColor)}>{config.label}</span>
          </div>
        )}

        {/* ✅ NEW: Requirements Checklist */}
        {/* Only shows when the user starts typing */}
        {showRequirements && currentValue.length > 0 && (
          <ul className="space-y-1 pt-2 pb-1">
            {requirements.map((req) => (
              <li key={req.label} className="flex items-center gap-2 text-xs transition-all duration-200">
                <Check className={cn(
                  "h-3.5 w-3.5 flex-shrink-0 transition-colors duration-200",
                  req.met ? "text-success" : "text-muted-foreground/30"
                )} />
                <span className={cn(
                  "transition-colors duration-200",
                  req.met ? "text-success font-medium" : "text-muted-foreground"
                )}>
                  {req.label}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
);
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };